import Vue from "vue";
import Vuex from "vuex";
import _ from "lodash";
import createPersistedState from "vuex-persistedstate";
import {
  getAssignedProjects,
  getAllProjects,
  getProjectPermissions,
  getProject,
  saveProject,
  saveEndUserProject,
  getEstimateSummary
} from "@/api/project-api";
import {
  getAccessToken,
  signUp,
  getProfile,
  User,
  debugToken,
  saveProfile
} from "@/api/user-api";
import { Project } from "@/api/project-api";
import {
  getProjectMembers,
  appendProjectMember,
  removeProjectMember
} from "@/api/project-member-api";
import {
  getServiceUsers,
  getServicePermissions,
  getUserServices
} from "@/api/service-api";

import Service from "@/models/service";

import task from "./task";
import member from "./project-member";
import tasktype from "./task-type";
import folder_type from "./folder-default";
import estimate_payment_method from "./estimate-payment-method";
import message from "./message";
import { estimateTemplate } from "./estimate-template";

import { saveIcon } from "@/api/icon-api";
import User2 from "@/models/user";
import ProjectFormData from "@/api/models/ProjectFormData";
import ProjectFormEndUserData from "@/api/models/ProjectFormEndUserData";
import * as api from "@/api";
import { client } from "@/api/request";
import StaffApi from "@/api/staff-api";
import ServiceRoleApi from "@/api/service-role-api";
import SaveStaffRequest from "@/usecases/requests/save-staff-request";
import SaveStaffCommand from "@/usecases/commands/save-staff";
import ServiceUserApi from "@/api/service-user-api";
import EditStaffQuery from "@/usecases/queries/edit-staff-query";
import VendorApi from "@/api/gateways/vendor-api";
import VendorStaffApi from "@/api/gateways/vendor-staff-api";
import TaskTypeApi from "@/api/gateways/task-type-api";
import ProjectFolderDefaultApi from "@/api/gateways/folder-default-api";
import { getServiceTaskTypes } from "@/servicetask/api/service-tasks-api";
import SelectOption from "@/common/ui/presenters/select-option";
import RootState from "./root-state";
import { getExpenseCategories } from "@/api/cost/expense-categories-api";
import UserServiceApi from "@/api/gateways/user-service-api";
import StampFormData from "@/api/models/StampFormData";
import { saveStamp } from "@/api/stamp-api";
import { getStatusNewProject } from "@/api/project-api";
import EstimateApi from "@/api/orders/estimate-api";

Vue.use(Vuex);

const estimateApi = new EstimateApi(client);

// 画面遷移で要らなくなったデータを削除するべきか？

export default new Vuex.Store<RootState>({
  modules: {
    task,
    tasktype,
    folder_type,
    estimate_payment_method,
    member,
    estimateTemplate,
    message
  },
  state: {
    serviceId: "",
    expenseCategories: [],
    onMobile: false,
    accessToken: "",
    projectId: "",
    project: null,
    taskDefaults: null,
    user: new User(),
    service: new Service(),
    assignedProjects: [],
    activeProjects: [],
    serviceProjects: [],
    projectMembers: [],
    serviceUsers: [],
    servicePermissions: [],
    projectPermissions: [],
    projectSidebarExpanded: true,
    services: [],
    serviceTaskTypes: [],
    estimates: []
  },
  getters: {
    serviceTaskTypeOptions(state): SelectOption[] {
      return state.serviceTaskTypes.map(t => ({
        text: t.name,
        value: String(t.id)
      }));
    },
    expenseCategoryOptions(state): SelectOption[] {
      return state.expenseCategories.map(c => ({
        text: c.name,
        value: String(c.id)
      }));
    },
    projects(state): Project[] {
      return _.unionBy(
        state.assignedProjects,
        state.serviceProjects,
        "projectId"
      );
    },
    permissions: state => [
      ...state.servicePermissions.map(p => `service/${p.identifier}`),
      ...state.projectPermissions.map(p => `project/${p.identifier}`)
    ],
    user(state) {
      if (!state.accessToken) {
        return new User2();
      }
      return new User2({
        id: state.user.userId,
        name: state.user.name,
        icon: state.user.icon ? state.user.icon.imageURL : "",
        email: state.user.email,
        signedIn: true,
        permissions: [
          ...state.servicePermissions.map(p => `service/${p.identifier}`),
          ...state.projectPermissions.map(p => `project/${p.identifier}`)
        ]
      });
    },
    // TODO: このへんStoreから外す
    api() {
      return client;
    },
    taskTypeApi() {
      return new TaskTypeApi(client);
    },
    folderTypeApi() {
      return new ProjectFolderDefaultApi(client);
    },
    staffApi() {
      return new StaffApi(client);
    },
    serviceRoleApi() {
      return new ServiceRoleApi(client);
    },
    serviceUserApi() {
      return new ServiceUserApi(client);
    },
    vendorApi() {
      return new VendorApi(client);
    },
    vendorStaffApi() {
      return new VendorStaffApi(client);
    },

    onMobile(state) {
      return state.onMobile;
    }
  },
  mutations: {
    project(state, project) {
      state.project = project;
    },
    taskDefaults(state, taskDefaults) {
      state.taskDefaults = taskDefaults;
    },
    projectMembers(state, members) {
      state.projectMembers = members;
    },
    projectPermissions(state, permissions) {
      state.projectPermissions = permissions;
    },
    // stateに直接代入するとpersistされない
    saveToken(state, token) {
      state.accessToken = token;
      api.setToken(token);
    },
    onMobile(state) {
      state.onMobile = true;
    },
    token(state, token) {
      state.accessToken = token;
      api.setToken(token);
    },
    serviceId(state, serviceId) {
      state.serviceId = serviceId;
    },
    service(state, service) {
      state.service = service;
    },
    services(state, services) {
      state.services = services;
    },
    reset(state) {
      state.accessToken = "";
      api.resetToken();
      state.user = new User();
      state.serviceId = "";
      state.service = new Service();
      state.assignedProjects = [];
      state.serviceProjects = [];
    },
    resetExpenseCategories(state, expenseCategories) {
      state.expenseCategories = expenseCategories;
    },
    setViewallServiceTask(state, viewAllServiceTasks: boolean) {
      state.service.viewAllServiceTasks = viewAllServiceTasks;
    },
    updateStatusProject(state, data) {
      if (state.project) {
        state.project.colorName = data.colorName;
        state.project.statusProject = data.statusProject;
        state.project.breadCrumb = data.breadCrumb;
      }
    },
    updateContractDateProject(state, contractDate) {
      if (state.project && state.project.contractDate !== contractDate) {
        state.project.contractDate = contractDate;
      }
    },
    setProjectEstimateExpenseSummary(state, data) {
      if (state.project) {
        state.project.estimateExpenseSummary = data;
      }
    },
    updateSettlementMonth(state, settlementMonth) {
      state.service.settlementMonth = settlementMonth;
    },
    estimates(state, estimates) {
      state.estimates = estimates;
    },
    updateSchdeduleNotification(state, value) {
      state.service.scheduleNotification = value;
    }
  },
  actions: {
    /**
     * サービスを設定する
     */
    async autoSelectService({ state, getters, dispatch }) {
      const serviceApi = new UserServiceApi(getters.api);
      const userServices = await getUserServices();
      const userServiceActive = userServices.filter(item => {
        return item.disabled == false;
      });
      const token = await serviceApi.login(userServiceActive[0].id);
      state.accessToken = token;
      api.setToken(token);
      await dispatch("initService", { serviceId: userServiceActive[0].id });

      if (
        window.location.pathname != "/services/select" &&
        userServiceActive.length > 1
      ) {
        window.location.href = "/services/select";
      }
    },
    async initService({ commit, dispatch }, { serviceId }) {
      commit("serviceId", serviceId);
      dispatch("init");
    },
    async initExpenseCategories({ commit }) {
      commit("resetExpenseCategories", await getExpenseCategories());
    },
    async loadService({ state, getters, commit }) {
      if (!state.serviceId) return;
      const serviceApi = new UserServiceApi(getters.api);
      const service = await serviceApi.find(Number(state.serviceId));
      if (!service) throw new Error("Service is not found.");
      commit("service", service);
    },
    async init({ dispatch }) {
      await dispatch("getUnreadMessage");
      await dispatch("initUserProfile");
      await dispatch("updateProjects");
      await dispatch("loadService");
      await dispatch("initServicePermissions");
      await dispatch("initServiceList");
      await dispatch("initServiceTaskTypes");
    },
    async signUp(_store, data: FormData) {
      await signUp(data);
    },
    async signIn({ commit, dispatch }, params) {
      commit("saveToken", await getAccessToken(params));
      await dispatch("autoSelectService");
      dispatch("init");
    },
    async signOut({ commit }) {
      commit("reset");
    },
    async debugToken({ state, dispatch }) {
      if (state.accessToken) {
        if (await debugToken()) {
          await dispatch("init");
        } else {
          await dispatch("signOut");
        }
      }
    },
    async updateProjects({ dispatch }) {
      dispatch("updateAssignedProjects");
      dispatch("updateServiceProjects");
    },
    async updateAssignedProjects({ state }) {
      state.assignedProjects = await getAssignedProjects();
      state.activeProjects = await getAssignedProjects("", "", 0, true);
    },
    async updateServiceProjects({ state }) {
      state.serviceProjects = await getAllProjects();
    },
    async changeCurrentProject({ dispatch, state }, projectId) {
      state.projectId = projectId;
      await dispatch("initProject", projectId);
      await dispatch("initProjectMembers", projectId);
      await dispatch("initProjectPermissions");
    },
    async leaveProject({ commit }) {
      commit("project", null);
      commit("projectMembers", []);
      commit("projectPermissions", []);
    },
    async initProject({ commit }, projectId) {
      const project = await getProject(projectId);
      commit("project", project);
      commit("taskDefaults", _.cloneDeep(project.taskDefaults));
    },
    async saveProject({ dispatch }, { project, order }) {
      const d = ProjectFormData(project, order);
      const id = await saveProject(d);
      await dispatch("initProject", id);
      await dispatch("updateProjects");
    },
    async saveEndUserProject(
      { dispatch },
      { project, order, company, office }
    ) {
      const d = ProjectFormEndUserData(project, order, company, office);
      const id = await saveEndUserProject(d);
      await dispatch("initProject", id);
      await dispatch("updateProjects");
    },
    async initProjectMembers({ state }, projectId) {
      state.projectMembers = await getProjectMembers(projectId);
    },
    async appendProjectMember({ dispatch }, { projectId, staffId }) {
      await appendProjectMember(projectId, staffId);
      await dispatch("initProject", projectId);
      await dispatch("updateProjects");
      await dispatch("initProjectMembers", projectId);
      await dispatch("initProjectPermissions");
    },
    async removeProjectMember({ dispatch }, { projectId, staffId }) {
      await removeProjectMember(projectId, staffId);
      await dispatch("initProject", projectId);
      await dispatch("updateProjects");
      await dispatch("initProjectMembers", projectId);
      await dispatch("initProjectPermissions");
    },
    async initServiceTaskTypes({ state }) {
      state.serviceTaskTypes = await getServiceTaskTypes();
    },
    async initUserProfile({ state }) {
      state.user = await getProfile();
    },
    async saveUserProfile({ dispatch }, data: FormData) {
      await saveProfile(data);
      await dispatch("initUserProfile");
    },
    async saveIcon({ dispatch }, id: number) {
      await saveIcon(id);
      await dispatch("initUserProfile");
    },
    async initServiceUsers({ state }) {
      state.serviceUsers = await getServiceUsers();
    },
    async initServicePermissions({ state }) {
      state.servicePermissions = await getServicePermissions();
    },
    async initProjectPermissions({ state }) {
      state.projectPermissions = await getProjectPermissions(state.projectId);
    },
    async initServiceList({ commit }) {
      const services = await getUserServices();
      commit("services", services);
    },
    async saveStaff({ getters }, req: SaveStaffRequest) {
      const c = new SaveStaffCommand(getters.staffApi, getters.serviceRoleApi);
      await c.handle(req);
    },
    async editStaff({ getters }, id: string) {
      const query = new EditStaffQuery(
        getters.staffApi,
        getters.serviceUserApi
      );
      return await query.handle({ id });
    },
    // eslint-disable-next-line
    async saveStamp({}, stamp) {
      const d = StampFormData(stamp);

      return await saveStamp(d);
    },
    setViewallServiceTask({ commit }, viewAllServiceTasks: boolean) {
      commit("setViewallServiceTask", viewAllServiceTasks);
    },

    async getStatusProject({ commit }) {
      const data = await getStatusNewProject(this.state.projectId);
      commit("updateStatusProject", data);
    },
    setContractDateProject({ commit }, contractDate: string) {
      commit("updateContractDateProject", contractDate);
    },
    async setProjectEstimateExpenseSummary({ commit }) {
      const data = await getEstimateSummary(this.state.projectId);
      commit("setProjectEstimateExpenseSummary", data);
    },
    setSettlementMonth({ commit }, settlementMonth: number) {
      commit("updateSettlementMonth", settlementMonth);
    },
    async loadEstimates({ commit }) {
      const estimates = await estimateApi.findAll();
      commit("estimates", estimates);
    },
    updateSchdeduleNotification({ commit }, value: boolean) {
      commit("updateSchdeduleNotification", value);
    }
  },
  plugins: [createPersistedState({ paths: ["accessToken", "serviceId"] })]
});
